/**
 * DeploymentPreviewPage — Universal App Shell
 * 
 * Works in TWO modes:
 * 1. PREVIEW MODE: Loaded in iframe from Deployment Studio with URL params
 * 2. STANDALONE MODE: Deployed app with window.__CONFIG__ injected by server
 * 
 * URL params (preview):
 *   ?agents=junior,kaida  — comma-separated agent IDs
 *   &db=true              — enable database panels
 *   &appName=MyApp        — app branding name
 *   &primary=#6366f1      — primary color
 * 
 * window.__CONFIG__ (standalone):
 *   { agents: [...], dbEnabled: true, appName: '...', apiUrl: '...', primaryColor: '...' }
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AgentChatWidget from '../components/Widget/AgentChatWidget';
import ConversationLinker from '../components/ConversationLinker';
import { Agent } from '../types';
import {
  Users, FolderOpen, MessageSquare, FileText, Settings,
  Plus, ChevronRight, ChevronLeft, Upload, Zap, AlertTriangle,
  Bell, LayoutDashboard, Calendar, Clock,
  Brain, Sparkles, Loader2, CheckCircle,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

// Read config from window.__CONFIG__ (standalone mode) or environment
const APP_CONFIG = (window as any).__CONFIG__ || {};
const CORE_API = APP_CONFIG.apiUrl || process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in';

// ── Types ──
interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  firm: string;
  created?: string;
}

interface Case {
  id: string;
  title: string;
  caseNumber: string;
  clientRecordId: string;
  clientName: string;
  court: string;
  status: string;
  notes: string;
  created?: string;
}

interface CaseDocument {
  id: string;
  fileName: string;
  originalName?: string;
  fileType: string;
  fileSize: number;
  description: string;
  ocrStatus?: string;
  ocrError?: string;
  uploadedAt: string;
}

interface ChatSession {
  id: string;
  query: string;
  response: string;
  agentId: string;
  tokensCharged: number;
  timestamp: string;
}

// ── In-memory temp database for preview mode ──
// In standalone mode, these are populated from server APIs
let tempClients: Client[] = [];
let tempCases: Case[] = [];
let tempDocuments: Record<string, CaseDocument[]> = {};
let tempSessions: ChatSession[] = [];

type Panel = 'chat' | 'dashboard' | 'clients' | 'cases' | 'case-detail' | 'conversations' | 'alerts' | 'settings' | 'thread-detail' | 'billing' | 'plugins';

interface ChatThread {
  id: string;
  title: string;
  linkedCaseId: string | null;
  linkedClientId: string | null;
  messages: { role: 'user' | 'assistant'; content: string; timestamp: string }[];
  createdAt: string;
}

let tempThreads: ChatThread[] = [];

// Conversation history for chain-of-thought (session memory)
let conversationHistory: { role: 'user' | 'assistant'; content: string }[] = [];

// Helper: detect standalone mode (deployed app with server APIs)
const isStandalone = !!(window as any).__CONFIG__?.agents;

const DeploymentPreviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  
  // Read from URL params (preview mode) or window.__CONFIG__ (standalone mode)
  const agentIds = APP_CONFIG.agents 
    ? APP_CONFIG.agents.map((a: any) => a.id) 
    : (searchParams.get('agents') || 'junior').split(',').filter(Boolean);
  const dbEnabled = APP_CONFIG.dbEnabled ?? (searchParams.get('db') === 'true');
  const appName = APP_CONFIG.appName || searchParams.get('appName') || 'Legal AI';
  const primaryColor = APP_CONFIG.primaryColor || searchParams.get('primary') || '#6366f1';

  const [agents, setAgents] = useState<Agent[]>([]);
  const isStandaloneMode = !!APP_CONFIG.agents; // true when running as deployed app with __CONFIG__
  const [activePanel, setActivePanel] = useState<Panel>('chat');
  const [tokenBalance, setTokenBalance] = useState<{ total: number; used: number; remaining: number; contract?: any } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigateTo = (panel: Panel) => {
    setActivePanel(panel);
    setSidebarOpen(false);
  };

  // Database state
  const [clients, setClients] = useState<Client[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [caseDocuments, setCaseDocuments] = useState<CaseDocument[]>([]);
  const [caseSessions, setCaseSessions] = useState<ChatSession[]>([]);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<ChatThread | null>(null);

  // Forms
  const [showClientForm, setShowClientForm] = useState(false);
  const [showCaseForm, setShowCaseForm] = useState(false);

  // Conversation linking
  const [pendingConversation, setPendingConversation] = useState<{ query: string; response: string; timestamp: string; files?: Array<{ mimeType: string; data: string; name: string }> } | null>(null);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);

  useEffect(() => {
    loadAgents();
    loadTokens();
    if (dbEnabled) loadPersistedData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load persisted data from server (standalone mode) or use temp arrays (preview)
  const loadPersistedData = async () => {
    if (!isStandalone) return;
    try {
      const [clientsRes, casesRes, convSessionsRes] = await Promise.all([
        fetch('/api/clients'),
        fetch('/api/cases'),
        fetch('/api/conversations/sessions').catch(() => ({ json: () => ({ sessions: [] }) })),
      ]);
      const clientsData = await clientsRes.json();
      const casesData = await casesRes.json();
      const convData = await (convSessionsRes as any).json();

      const loadedClients = (clientsData.clients || []).map((c: any) => ({
        id: c.id, name: c.name || '', email: c.email || '', phone: c.phone || '',
        firm: c.firm || '', created: c.createdAt || c.created || '',
      }));
      const loadedCases = (casesData.cases || []).map((c: any) => ({
        id: c.id, title: c.title || '', caseNumber: c.caseNumber || '',
        clientRecordId: c.clientRecordId || '', clientName: c.clientName || '',
        court: c.court || '', status: c.status || 'open', notes: c.notes || '',
        created: c.createdAt || c.created || '',
      }));

      tempClients = loadedClients;
      tempCases = loadedCases;
      setClients(loadedClients);
      setCases(loadedCases);

      // Build threads from conversation sessions
      const sessions = convData.sessions || [];
      const threads: ChatThread[] = sessions.map((s: any) => ({
        id: s.id || `sess-${s.createdAt}`,
        title: s.title || s.firstQuery?.slice(0, 50) || 'Conversation',
        linkedCaseId: s.caseId || null,
        linkedClientId: s.clientId || null,
        messages: [], // Loaded on demand when user clicks
        createdAt: s.createdAt || s.lastActivity || new Date().toISOString(),
      }));
      tempThreads = threads;
      setChatThreads(threads);

      // Fallback: if new API not available, try old sessions API
      if (threads.length === 0) {
        try {
          const sessionsRes = await fetch('/api/sessions');
          const sessionsData = await sessionsRes.json();
          const oldThreads: ChatThread[] = (sessionsData.sessions || [])
            .filter((s: any) => s.query)
            .map((s: any) => ({
              id: s.id?.toString() || `sess-${s.timestamp}`,
              title: (s.query || '').slice(0, 50) + ((s.query || '').length > 50 ? '...' : ''),
              linkedCaseId: s.caseId || null,
              linkedClientId: s.clientId || null,
              messages: [
                { role: 'user' as const, content: s.query || '', timestamp: s.timestamp || '' },
                ...(s.response ? [{ role: 'assistant' as const, content: s.response, timestamp: s.timestamp || '' }] : []),
              ],
              createdAt: s.timestamp || new Date().toISOString(),
            }));
          tempThreads = oldThreads;
          setChatThreads(oldThreads);
        } catch {}
      }
    } catch (e) {
      console.warn('Failed to load persisted data:', e);
    }
  };

  const loadAgents = async () => {
    // If agents are pre-loaded from config (standalone mode), use them directly
    if (APP_CONFIG.agents && APP_CONFIG.agents.length > 0) {
      setAgents(APP_CONFIG.agents);
      return;
    }
    try {
      const res = await fetch(`${CORE_API}/api/agents?role=lawyer`);
      const data = await res.json();
      const filtered = (data.agents || []).filter((a: Agent) => agentIds.includes(a.id));
      setAgents(filtered.length > 0 ? filtered : data.agents || []);
    } catch { /* silent */ }
  };

  const loadTokens = async () => {
    try {
      // In standalone mode (window.__CONFIG__ has agents), use local /api/tokens
      const isStandalone = !!(window as any).__CONFIG__?.agents;
      const res = await fetch(isStandalone ? '/api/tokens' : `${CORE_API}/api/billing/usage`);
      const data = await res.json();
      if (isStandalone) {
        // Standalone mode: /api/tokens returns { total, used, remaining, contract }
        setTokenBalance({
          total: data.total || 0,
          used: data.used || 0,
          remaining: data.remaining ?? 0,
          contract: data.contract || null,
        });
      } else {
        // Preview mode: CoreAPI billing/usage format
        setTokenBalance({
          total: data.usage?.totalTokensUsed || 0,
          used: data.usage?.totalTokensUsed || 0,
          remaining: data.usage?.tokensRemaining ?? 0,
        });
      }
    } catch { /* silent */ }
  };

  // ── Client CRUD ──
  const addClient = async (c: Omit<Client, 'id' | 'created'>) => {
    const sixDigitId = String(Math.floor(100000 + Math.random() * 900000));
    const newClient: Client = { ...c, id: sixDigitId, created: new Date().toISOString() };
    
    // Persist to server in standalone mode
    if (isStandalone) {
      try {
        const res = await fetch('/api/clients', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: c.name, email: c.email, phone: c.phone, firm: c.firm }),
        });
        const data = await res.json();
        if (data.success && data.client) newClient.id = data.client.id;
      } catch (e) { console.warn('Failed to persist client:', e); }
    }

    tempClients = [newClient, ...tempClients];
    setClients([...tempClients]);
    setShowClientForm(false);
    toast.success('Client added');
    return newClient;
  };

  // ── Case CRUD ──
  const addCase = async (c: Omit<Case, 'id' | 'created'>) => {
    const newCase: Case = { ...c, id: `cs-${Date.now()}`, created: new Date().toISOString() };
    
    if (isStandalone) {
      try {
        const res = await fetch('/api/cases', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: c.title, caseNumber: c.caseNumber, clientRecordId: c.clientRecordId, clientName: c.clientName, court: c.court, status: c.status, notes: c.notes }),
        });
        const data = await res.json();
        if (data.success && data.case) newCase.id = data.case.id;
      } catch (e) { console.warn('Failed to persist case:', e); }
    }

    tempCases = [newCase, ...tempCases];
    setCases([...tempCases]);
    setShowCaseForm(false);
    toast.success('Case created');
    return newCase;
  };

  const openCase = (c: Case) => {
    setSelectedCase(c);
    setCaseDocuments(tempDocuments[c.id] || []);
    setCaseSessions(tempSessions.filter(s => s.id.includes(c.id)));
    setActivePanel('case-detail');
  };

  // ── File upload ──
  const uploadFile = async (caseId: string, file: File) => {
    const doc: CaseDocument = {
      id: `doc-${Date.now()}`,
      fileName: file.name,
      originalName: file.name,
      fileType: file.type,
      fileSize: file.size,
      description: '',
      ocrStatus: 'pending',
      uploadedAt: new Date().toISOString(),
    };

    if (isStandalone) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('description', '');
        const res = await fetch(`/api/cases/${caseId}/documents`, {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        if (data.success && data.document) {
          doc.id = data.document.id;
          doc.ocrStatus = data.document.ocrStatus || 'pending';
        }
      } catch (e) { console.warn('Failed to upload document:', e); }
    }

    if (!tempDocuments[caseId]) tempDocuments[caseId] = [];
    tempDocuments[caseId].push(doc);
    setCaseDocuments([...tempDocuments[caseId]]);
    toast.success(`${file.name} uploaded`);
  };

  const handleTokenUpdate = useCallback((tokens: { session: number; remaining: number | null }) => {
    if (tokens.remaining !== null) {
      setTokenBalance(prev => prev ? { ...prev, remaining: tokens.remaining! } : null);
    }
  }, []);

  // Database context provider — gives the AI agent access to clients/cases data
  // In standalone mode, the server already injects full SQLite context into queries.
  // This function provides additional frontend-side context (conversation history, temp data in preview mode).
  const getDbContext = useCallback((): string | null => {
    if (!dbEnabled) return null;

    let ctx = '';
    
    // In preview mode (no server), use temp arrays
    if (!isStandalone) {
      if (tempClients.length === 0 && tempCases.length === 0 && conversationHistory.length === 0) return null;
      
      if (tempClients.length > 0) {
        ctx += `You have ${tempClients.length} client(s):\n`;
        tempClients.forEach((c, i) => {
          ctx += `  ${i + 1}. ${c.name}${c.email ? ' (' + c.email + ')' : ''}${c.firm ? ' \u2014 ' + c.firm : ''}${c.phone ? ', Phone: ' + c.phone : ''}\n`;
        });
      }
      if (tempCases.length > 0) {
        ctx += `\nYou have ${tempCases.length} case(s):\n`;
        tempCases.forEach((c, i) => {
          ctx += `  ${i + 1}. "${c.title}" \u2014 Status: ${c.status}${c.clientName ? ', Client: ' + c.clientName : ''}${c.court ? ', Court: ' + c.court : ''}${c.caseNumber ? ', Case #: ' + c.caseNumber : ''}${c.notes ? ', Notes: ' + c.notes : ''}\n`;
        });
      }
      ctx += `\n[INSTRUCTION: If this query relates to a specific client or case, mention which one. If the user asks about their data, answer from context above. After answering a legal query, suggest: "Would you like me to save this conversation to a specific case?"]`;
    }
    
    // Add frontend conversation history (supplements server-side memory)
    if (conversationHistory.length > 0) {
      ctx += `\n\n[SESSION CONTEXT - Current conversation thread:]\n`;
      conversationHistory.slice(-6).forEach(msg => {
        ctx += `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content.slice(0, 200)}\n`;
      });
      ctx += `[END SESSION]`;
    }
    
    return ctx || null;
  }, [dbEnabled]);

  // Promote chat-attached files into a case so its micro-agent learns them.
  const promoteFilesToCase = useCallback((caseId: string, files?: Array<{ mimeType: string; data: string; name: string }>) => {
    if (!isStandalone || !caseId || !files || files.length === 0) return;
    fetch(`/api/cases/${caseId}/attach-files`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files }),
    })
      .then(r => r.json())
      .then(d => { if (d.added) toast.success(`${d.added} file(s) added to case — micro-agent is learning them.`); })
      .catch(() => {});
  }, [isStandalone]);

  // Save a chat thread after each conversation — persists to SQLite in standalone mode
  const saveThread = useCallback((query: string, _response: string, linkedCaseId: string | null = null, linkedClientId: string | null = null) => {
    const sessionId = `thread-${Date.now()}`;
    const thread: ChatThread = {
      id: sessionId,
      title: query.slice(0, 50) + (query.length > 50 ? '...' : ''),
      linkedCaseId,
      linkedClientId,
      messages: [
        { role: 'user', content: query, timestamp: new Date().toISOString() },
        { role: 'assistant', content: _response, timestamp: new Date().toISOString() },
      ],
      createdAt: new Date().toISOString(),
    };
    tempThreads = [thread, ...tempThreads];
    setChatThreads([...tempThreads]);

    // Persist to server using new conversations API
    if (isStandalone) {
      const agentId = agents[0]?.id || 'unknown';
      // Save user message
      fetch('/api/conversations/message', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, role: 'user', content: query, agentId, clientId: linkedClientId || '', caseId: linkedCaseId || '', type: 'text' }),
      }).catch(() => {});
      // Save assistant response
      fetch('/api/conversations/message', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, role: 'assistant', content: _response, agentId, clientId: linkedClientId || '', caseId: linkedCaseId || '', type: 'text' }),
      }).catch(() => {});
      // Also save to legacy sessions table
      fetch('/api/sessions/answer', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, agentId, query, response: _response, tokensCharged: 0, caseId: linkedCaseId || '' }),
      }).catch(() => {});
    }
  }, [agents]);

  const lowTokens = tokenBalance && tokenBalance.remaining <= 5;

  // ── Render ──
  return (
    <div className="flex h-screen overflow-hidden" style={{ '--primary': primaryColor } as any}>
      <Toaster position="top-right" />

      {/* Mobile top bar */}
      {dbEnabled && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-slate-900 border-b border-slate-700 px-4 py-3 flex items-center justify-between md:hidden">
          <button onClick={() => setSidebarOpen(true)} className="p-1 text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
          </button>
          <h2 className="text-sm font-bold text-white truncate">{appName}</h2>
          <div className="flex items-center gap-1">
            <Zap size={12} className="text-amber-400" />
            <span className="text-[10px] text-white font-bold">{tokenBalance ? tokenBalance.remaining.toFixed(0) : '—'}</span>
          </div>
        </div>
      )}

      {/* Sidebar overlay backdrop (mobile) */}
      {dbEnabled && sidebarOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar — responsive: overlay on mobile, fixed on desktop */}
      {dbEnabled && (
        <div className={`fixed md:relative z-50 md:z-auto inset-y-0 left-0 w-64 md:w-60 bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col flex-shrink-0 shadow-xl transform transition-transform duration-200 ease-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
          {/* App name */}
          <div className="p-5 border-b border-slate-700/50 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-white truncate tracking-wide">{appName}</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">Legal Practice Management</p>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-1 text-slate-400 hover:text-white md:hidden">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider px-3 pt-2 pb-1">Practice</div>
            {dbEnabled && <NavItem icon={<LayoutDashboard size={15} />} label="Dashboard" active={activePanel === 'dashboard'} onClick={() => navigateTo('dashboard')} />}
            <NavItem icon={<MessageSquare size={15} />} label="AI Counsel" active={activePanel === 'chat'} onClick={() => navigateTo('chat')} />
            {dbEnabled && <NavItem icon={<Users size={15} />} label="Clients" active={activePanel === 'clients'} onClick={() => navigateTo('clients')} />}
            {dbEnabled && <NavItem icon={<FolderOpen size={15} />} label="Cases" active={activePanel === 'cases' || activePanel === 'case-detail'} onClick={() => navigateTo('cases')} />}
            {dbEnabled && <NavItem icon={<Bell size={15} />} label="Alerts & Deadlines" active={activePanel === 'alerts'} onClick={() => navigateTo('alerts')} />}
            
            {/* Chat threads */}
            {chatThreads.length > 0 && (
              <>
                <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider px-3 pt-5 pb-1">Recent Consultations</div>
                {chatThreads.slice(0, 8).map(t => (
                  <button key={t.id} onClick={() => { setSelectedThread(t); setActivePanel('thread-detail'); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 truncate transition-colors">
                    <MessageSquare size={10} className="flex-shrink-0 text-slate-500" />
                    <span className="truncate flex-1 text-left">{t.title}</span>
                    {t.linkedCaseId && <span className="text-[8px] text-blue-400 flex-shrink-0">📁</span>}
                    {t.linkedClientId && !t.linkedCaseId && <span className="text-[8px] text-emerald-400 flex-shrink-0">👤</span>}
                  </button>
                ))}
              </>
            )}

            <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider px-3 pt-5 pb-1">Integrations</div>
            <NavItem icon={<Sparkles size={15} />} label="Plugins" active={activePanel === 'plugins'} onClick={() => navigateTo('plugins')} />

            <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider px-3 pt-5 pb-1">Account</div>
            <NavItem icon={<Zap size={15} />} label="Billing & Usage" active={activePanel === 'billing'} onClick={() => navigateTo('billing')} />
            <NavItem icon={<Settings size={15} />} label="Settings" active={activePanel === 'settings'} onClick={() => navigateTo('settings')} />
            <button onClick={() => { fetch('/auth/logout', { method: 'POST' }).then(() => window.location.href = '/login.html'); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-red-400 hover:bg-red-500/10 mt-2 transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Logout
            </button>
          </nav>

          {/* Token balance */}
          <div className="p-4 border-t border-slate-700/50 bg-slate-900/50">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Zap size={12} className="text-amber-400" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Credits</span>
            </div>
            <div className={`text-xl font-bold ${lowTokens ? 'text-red-400' : 'text-white'}`}>
              {tokenBalance ? tokenBalance.remaining.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
            </div>
            <div className="text-[9px] text-slate-500 mt-0.5">tokens remaining</div>
            {lowTokens && (
              <div className="flex items-center gap-1 mt-2 text-[10px] text-red-400 bg-red-500/10 px-2 py-1 rounded-md">
                <AlertTriangle size={10} /> Low balance — contact admin
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 flex flex-col overflow-hidden ${dbEnabled ? 'pt-[52px] md:pt-0' : ''}`}>
        {/* Low token warning banner */}
        {lowTokens && (
          <div className="bg-amber-50 border-b border-amber-200 px-5 py-2.5 flex items-center gap-2.5 text-xs text-amber-800 flex-shrink-0">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="font-semibold">Low credit balance</span>
            <span className="text-amber-600">— {tokenBalance?.remaining.toFixed(2)} tokens remaining. Please contact your administrator for recharge.</span>
          </div>
        )}

        {/* Dashboard panel */}
        {activePanel === 'dashboard' && dbEnabled && (
          <DashboardPanel appName={appName} onNavigate={setActivePanel} />
        )}

        {/* Chat panel */}
        {activePanel === 'chat' && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-hidden">
              <AgentChatWidget
                apiUrl={APP_CONFIG.apiUrl || undefined}
                agents={agents}
                initialAgentId={agentIds[0]}
                showSidebar={!dbEnabled}
                showHeader={true}
                height="100%"
                onTokenUpdate={handleTokenUpdate}
                getDbContext={dbEnabled ? getDbContext : undefined}
                onMessageComplete={(query, response, files) => {
                  // Save to conversation history (chain of thought)
                  conversationHistory.push({ role: 'user', content: query });
                  conversationHistory.push({ role: 'assistant', content: response });
                  // Keep last 10 exchanges for context
                  if (conversationHistory.length > 20) conversationHistory = conversationHistory.slice(-20);
                  // Always save thread immediately (appears in sidebar)
                  saveThread(query, response, null, null);
                  // Refresh token balance after query
                  loadTokens();
                  // Show linking prompt if DB enabled (for optional client/case linking)
                  if (dbEnabled) {
                    setPendingConversation({ query, response, timestamp: new Date().toISOString(), files: files && files.length ? files : undefined });
                    setShowLinkPrompt(true);
                  }
                }}
              />
            </div>
            {/* Conversation linker prompt */}
            {showLinkPrompt && pendingConversation && dbEnabled && (
              <ConversationLinker
                clients={clients.map(c => ({ id: c.id, name: c.name }))}
                cases={cases.map(c => ({ id: c.id, title: c.title, clientName: c.clientName, clientRecordId: c.clientRecordId }))}
                onLink={(type, id) => {
                  if (pendingConversation) {
                    saveThread(pendingConversation.query, pendingConversation.response, type === 'case' ? id : null, type === 'client' ? id : null);
                    // Promote any chat-attached files into the case → micro-agent learns them.
                    if (type === 'case') promoteFilesToCase(id, pendingConversation.files);
                    toast.success(`Linked to ${type}`);
                  }
                  setShowLinkPrompt(false);
                  setPendingConversation(null);
                }}
                onCreateClient={(name) => {
                  const newClient: Client = { id: `cl-${Date.now()}`, name, email: '', phone: '', firm: '', created: new Date().toISOString() };
                  // Persist
                  if (isStandalone) {
                    fetch('/api/clients', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) })
                      .then(r => r.json()).then(d => { if (d.client) newClient.id = d.client.id; }).catch(() => {});
                  }
                  tempClients = [newClient, ...tempClients];
                  setClients([...tempClients]);
                  if (pendingConversation) {
                    saveThread(pendingConversation.query, pendingConversation.response, null, newClient.id);
                    toast.success(`Client "${name}" created & linked`);
                  }
                  setShowLinkPrompt(false);
                  setPendingConversation(null);
                }}
                onCreateCase={(title, clientId) => {
                  const clientName = tempClients.find(c => c.id === clientId)?.name || '';
                  const newCase: Case = { id: `cs-${Date.now()}`, title, caseNumber: '', clientRecordId: clientId, clientName, court: '', status: 'open', notes: '', created: new Date().toISOString() };
                  const pendingFiles = pendingConversation?.files;
                  // Persist
                  if (isStandalone) {
                    fetch('/api/cases', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title, clientRecordId: clientId, clientName }) })
                      .then(r => r.json()).then(d => {
                        if (d.case) {
                          newCase.id = d.case.id;
                          // Promote chat-attached files into the newly created case
                          promoteFilesToCase(d.case.id, pendingFiles);
                        }
                      }).catch(() => {});
                  }
                  tempCases = [newCase, ...tempCases];
                  setCases([...tempCases]);
                  if (pendingConversation) {
                    saveThread(pendingConversation.query, pendingConversation.response, newCase.id, clientId || null);
                    toast.success(`Case "${title}" created & linked`);
                  }
                  setShowLinkPrompt(false);
                  setPendingConversation(null);
                }}
                onUploadDocument={(caseId, file) => {
                  uploadFile(caseId, file);
                }}
                onSkip={() => {
                  if (pendingConversation) {
                    saveThread(pendingConversation.query, pendingConversation.response, null, null);
                  }
                  setShowLinkPrompt(false);
                  setPendingConversation(null);
                }}
              />
            )}
          </div>
        )}

        {/* Clients panel */}
        {activePanel === 'clients' && (
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Clients</h2>
                <p className="text-xs text-slate-500 mt-0.5">{clients.length} registered client{clients.length !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setShowClientForm(true)} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-800 transition-colors shadow-sm">
                <Plus size={13} /> Add Client
              </button>
            </div>

            {showClientForm && <ClientForm onSave={addClient} onCancel={() => setShowClientForm(false)} />}

            {clients.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Users size={40} className="mx-auto mb-3 opacity-40" />
                <p className="font-medium">No clients yet</p>
                <p className="text-xs">Add your first client to get started</p>
              </div>
            ) : (
              <div className="space-y-2">
                {clients.map(c => {
                  const clientThreads = chatThreads.filter(t => t.linkedClientId === c.id);
                  const clientCases = cases.filter(cs => cs.clientRecordId === c.id);
                  return (
                    <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-3 hover:border-indigo-300 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-900">{c.name}</span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-mono">#{c.id}</span>
                        {clientCases.length > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded font-semibold">{clientCases.length} case{clientCases.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500">{c.email}{c.firm ? ` • ${c.firm}` : ''}</div>

                      {/* Linked cases — click to open the case detail screen */}
                      {clientCases.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="text-[9px] font-semibold text-gray-400 uppercase mb-1">Cases ({clientCases.length})</div>
                          {clientCases.map(cs => (
                            <div key={cs.id} onClick={() => openCase(cs)}
                              className="flex items-center gap-2 py-1.5 px-1 cursor-pointer hover:bg-indigo-50 rounded transition-colors group">
                              <FolderOpen size={11} className="text-indigo-400 flex-shrink-0" />
                              <span className="text-[11px] text-gray-700 font-medium truncate flex-1 group-hover:text-indigo-700">{cs.title}</span>
                              <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-semibold ${cs.status === 'closed' ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-600'}`}>{cs.status}</span>
                              <ChevronRight size={11} className="text-gray-300 flex-shrink-0 group-hover:text-indigo-400" />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Linked conversations */}
                      {clientThreads.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="text-[9px] font-semibold text-gray-400 uppercase mb-1">Conversations ({clientThreads.length})</div>
                          {clientThreads.slice(0, 3).map(t => (
                            <div key={t.id} onClick={() => setActivePanel('chat')}
                              className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 rounded px-1">
                              <MessageSquare size={9} className="text-indigo-400" />
                              <span className="text-[10px] text-gray-600 truncate flex-1">{t.title}</span>
                              <span className="text-[8px] text-gray-400">{new Date(t.createdAt).toLocaleDateString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Cases panel */}
        {activePanel === 'cases' && (
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Cases</h2>
                <p className="text-xs text-slate-500 mt-0.5">{cases.length} active case{cases.length !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setShowCaseForm(true)} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-800 transition-colors shadow-sm">
                <Plus size={13} /> New Case
              </button>
            </div>

            {showCaseForm && <CaseForm clients={clients} onSave={addCase} onCancel={() => setShowCaseForm(false)} />}

            {cases.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <FolderOpen size={40} className="mx-auto mb-3 opacity-40" />
                <p className="font-medium">No cases yet</p>
                <p className="text-xs">Create a case to track conversations and documents</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cases.map(c => (
                  <div key={c.id} onClick={() => openCase(c)} className="bg-white border border-gray-200 rounded-xl p-3 hover:border-indigo-300 cursor-pointer transition-colors flex items-center gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-gray-900">{c.title}</div>
                      <div className="text-[11px] text-gray-500">{c.clientName}{c.court ? ` • ${c.court}` : ''}</div>
                    </div>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${
                      c.status === 'open' ? 'bg-green-100 text-green-700' :
                      c.status === 'in-progress' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{c.status}</span>
                    <ChevronRight size={14} className="text-gray-300" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Case detail panel */}
        {activePanel === 'case-detail' && selectedCase && (
          <CaseDetailPanel
            caseData={selectedCase}
            documents={caseDocuments}
            sessions={chatThreads.filter(t => t.linkedCaseId === selectedCase.id).map(t => ({
              id: t.id,
              query: t.messages[0]?.content || '',
              response: t.messages[1]?.content || '',
              agentId: 'agent',
              tokensCharged: 0,
              timestamp: t.createdAt,
            }))}
            onUpload={(file) => uploadFile(selectedCase.id, file)}
            onBack={() => setActivePanel('cases')}
          />
        )}

        {/* Billing panel — shows per-conversation token usage */}
        {activePanel === 'billing' && (
          <BillingPanel tokenBalance={tokenBalance} />
        )}

        {/* Alerts panel */}
        {activePanel === 'alerts' && dbEnabled && (
          <AlertsPanel />
        )}

        {/* Settings panel */}
        {activePanel === 'settings' && (
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            <h2 className="text-xl font-bold text-slate-900 mb-5">Settings</h2>
            <div className="space-y-4 max-w-lg">
              <SettingsCard title="⚡ Token Balance">
                <div className="text-2xl font-bold text-indigo-600 mb-1">{tokenBalance?.remaining.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || '—'}</div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" style={{ width: tokenBalance && tokenBalance.total > 0 ? `${Math.min((tokenBalance.used / tokenBalance.total) * 100, 100)}%` : '0%' }} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-500">
                  <span>Used: {tokenBalance?.used.toFixed(2) || '0'}</span>
                  <span>Total: {tokenBalance?.total || '0'}</span>
                </div>
              </SettingsCard>
              <SettingsCard title="💰 Billing">
                <div className="text-xs text-gray-500 space-y-1.5">
                  <div className="flex justify-between"><span>Model</span><span className="font-semibold text-gray-900">Pay per use</span></div>
                  <div className="flex justify-between"><span>Rate</span><span className="font-semibold text-gray-900">₹{tokenBalance?.contract?.costPerToken || 50} / token</span></div>
                  <div className="flex justify-between"><span>Tokens deducted</span><span className="font-semibold text-gray-900">Per query</span></div>
                  {tokenBalance?.contract?.oneTimeCost > 0 && (
                    <div className="flex justify-between"><span>One-time setup</span><span className="font-semibold text-gray-900">₹{tokenBalance?.contract?.oneTimeCost}</span></div>
                  )}
                </div>
              </SettingsCard>
              <SettingsCard title="🟢 API Health">
                <ApiHealthCheck />
              </SettingsCard>
              <SettingsCard title="🤖 Active Agents">
                <div className="text-xs text-gray-600 space-y-1">
                  {agents.map(a => (
                    <div key={a.id} className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-md bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-[9px]">{a.icon}</span>
                      <span className="font-medium">{a.name}</span>
                      <span className="text-[9px] text-gray-400">({(a.capabilities || []).length} capabilities)</span>
                    </div>
                  ))}
                </div>
              </SettingsCard>
              {isStandaloneMode && <NetworkAccessCard />}
            </div>
          </div>
        )}

        {/* Plugins panel — integrations with external platforms */}
        {activePanel === 'plugins' && (
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Plugins & Integrations</h2>
                  <p className="text-xs text-slate-500">Use your AI assistant inside other apps</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Adobe Acrobat / PDF Reader */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-2xl">📄</div>
                    <div>
                      <h3 className="font-bold text-sm text-gray-900">Adobe Acrobat Reader</h3>
                      <span className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">Ready</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3 leading-relaxed">Analyze PDF documents, summarize judgments, extract key clauses, and ask questions about any PDF — directly from Acrobat Reader.</p>
                  <div className="space-y-1.5 mb-4">
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Summarize long legal documents</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Extract key dates & parties</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Analyze contract clauses</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Ask questions about the PDF</div>
                  </div>
                  <button onClick={() => toast.success('Adobe plugin instructions copied!')} className="w-full py-2.5 bg-red-600 text-white rounded-xl text-xs font-semibold hover:bg-red-700 transition-colors">
                    Install Plugin
                  </button>
                </div>

                {/* Microsoft Office */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-2xl">📝</div>
                    <div>
                      <h3 className="font-bold text-sm text-gray-900">Microsoft Office</h3>
                      <span className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">Ready</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3 leading-relaxed">Draft legal documents in Word, analyze data in Excel, and create presentations — with AI assistance built right into Office.</p>
                  <div className="space-y-1.5 mb-4">
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Draft notices & petitions in Word</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Case tracking templates in Excel</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Legal research in OneNote</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Court presentation slides</div>
                  </div>
                  <button onClick={() => toast.success('Office plugin instructions copied!')} className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-xs font-semibold hover:bg-blue-700 transition-colors">
                    Install Plugin
                  </button>
                </div>

                {/* ChatGPT */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-2xl">🤖</div>
                    <div>
                      <h3 className="font-bold text-sm text-gray-900">ChatGPT</h3>
                      <span className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">Ready</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3 leading-relaxed">Use your legal AI agent directly inside ChatGPT. Ask legal questions and get expert responses powered by your dedicated agent.</p>
                  <div className="space-y-1.5 mb-4">
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Works inside ChatGPT interface</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Indian law specialization</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Same token billing</div>
                  </div>
                  <button onClick={() => toast.success('ChatGPT plugin details copied!')} className="w-full py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-semibold hover:bg-emerald-700 transition-colors">
                    Install Plugin
                  </button>
                </div>

                {/* Claude */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-2xl">🧠</div>
                    <div>
                      <h3 className="font-bold text-sm text-gray-900">Claude (MCP)</h3>
                      <span className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">Ready</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3 leading-relaxed">Add your legal AI as a tool inside Claude Desktop. Claude can invoke your agent for legal research, drafting, and case analysis.</p>
                  <div className="space-y-1.5 mb-4">
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Legal research tool</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Document drafting tool</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Section analysis tool</div>
                  </div>
                  <button onClick={() => toast.success('Claude MCP config copied!')} className="w-full py-2.5 bg-amber-600 text-white rounded-xl text-xs font-semibold hover:bg-amber-700 transition-colors">
                    Install Plugin
                  </button>
                </div>

                {/* Instagram */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center text-2xl">📸</div>
                    <div>
                      <h3 className="font-bold text-sm text-gray-900">Instagram DM</h3>
                      <span className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">Ready</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3 leading-relaxed">Auto-reply to Instagram DMs with your AI agent. Your followers can get instant legal guidance through Instagram messages.</p>
                  <div className="space-y-1.5 mb-4">
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Auto-reply to DMs</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> Conversation memory</div>
                    <div className="text-[10px] text-gray-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-green-500" /> 24/7 availability</div>
                  </div>
                  <button onClick={() => toast.success('Instagram bot setup details copied!')} className="w-full py-2.5 bg-pink-600 text-white rounded-xl text-xs font-semibold hover:bg-pink-700 transition-colors">
                    Install Plugin
                  </button>
                </div>
              </div>

              <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                <p className="text-xs text-indigo-700"><strong>💡 How billing works:</strong> All plugins use tokens from your existing balance. No separate billing — every query from any channel (ChatGPT, Claude, Instagram, Office, PDF) deducts from the same token pool.</p>
              </div>
            </div>
          </div>
        )}

        {/* Thread detail panel — shows full conversation with timestamps */}
        {activePanel === 'thread-detail' && selectedThread && (
          <ThreadDetailPanel
            thread={selectedThread}
            clients={clients}
            cases={cases}
            isStandalone={isStandalone}
            onBack={() => setActivePanel('chat')}
          />
        )}
      </div>
    </div>
  );
};

// ── Sub-components ──

const NetworkAccessCard: React.FC = () => {
  const [networkInfo, setNetworkInfo] = useState<{ networkAddresses: { name: string; address: string; url: string }[]; instructions: string[] } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch('/api/network-info').then(r => r.json()).then(d => {
      if (d.success) setNetworkInfo(d);
    }).catch(() => {});
  }, []);

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!networkInfo || networkInfo.networkAddresses.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-slate-800 mb-3">📱 Access from Phone</h4>
        <p className="text-xs text-gray-500">Connect to a WiFi network to access from other devices.</p>
      </div>
    );
  }

  const primaryUrl = networkInfo.networkAddresses[0].url;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-800 mb-3">📱 Access from Phone / Tablet</h4>
      <div className="space-y-3">
        {networkInfo.networkAddresses.map((addr, i) => (
          <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <div>
              <div className="text-xs font-mono font-semibold text-indigo-600">{addr.url}</div>
              <div className="text-[10px] text-gray-400">{addr.name}</div>
            </div>
            <button onClick={() => copyUrl(addr.url)} className="text-[10px] px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-medium hover:bg-indigo-200">
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        ))}
        {/* QR Code (simple text-based representation) */}
        <div className="flex items-center justify-center p-4 bg-slate-50 rounded-lg">
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(primaryUrl)}`} alt="QR Code" className="w-28 h-28" />
        </div>
        <div className="text-[10px] text-center text-gray-400">Scan with your phone camera</div>
        <div className="border-t border-slate-100 pt-3">
          <div className="text-[10px] font-semibold text-slate-600 mb-1.5">How to access from your phone:</div>
          <ol className="text-[10px] text-gray-500 space-y-1 list-decimal list-inside">
            {networkInfo.instructions.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        </div>
      </div>
    </div>
  );
};

const ApiHealthCheck: React.FC = () => {
  const [health, setHealth] = useState<{ status: string; latency: number } | null>(null);
  useEffect(() => {
    const check = async () => {
      const start = Date.now();
      try {
        const res = await fetch('/api/agents');
        if (res.ok) {
          setHealth({ status: 'healthy', latency: Date.now() - start });
        } else {
          setHealth({ status: 'degraded', latency: Date.now() - start });
        }
      } catch {
        setHealth({ status: 'offline', latency: 0 });
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!health) return <div className="text-[10px] text-gray-400">Checking...</div>;
  return (
    <div className="text-xs space-y-1">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${health.status === 'healthy' ? 'bg-green-500' : health.status === 'degraded' ? 'bg-amber-500' : 'bg-red-500'}`} />
        <span className="font-medium text-gray-700 capitalize">{health.status}</span>
        <span className="text-[9px] text-gray-400">{health.latency}ms</span>
      </div>
      <div className="text-[10px] text-gray-400">AI service • Last checked: just now</div>
    </div>
  );
};

const NavItem: React.FC<{ icon: React.ReactNode; label: string; active: boolean; onClick: () => void }> = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${active ? 'bg-white/10 text-white shadow-sm border border-white/10' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}>
    {icon} {label}
  </button>
);

const SettingsCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
    <h4 className="text-sm font-semibold text-slate-800 mb-3">{title}</h4>
    {children}
  </div>
);

const ClientForm: React.FC<{ onSave: (c: Omit<Client, 'id' | 'created'>) => void; onCancel: () => void }> = ({ onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [firm, setFirm] = useState('');
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 space-y-2">
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Client name *" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <input value={firm} onChange={e => setFirm(e.target.value)} placeholder="Firm / Organization" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <div className="flex gap-2">
        <button onClick={() => { if (name) onSave({ name, email, phone, firm }); }} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold">Save</button>
        <button onClick={onCancel} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600">Cancel</button>
      </div>
    </div>
  );
};

const CaseForm: React.FC<{ clients: Client[]; onSave: (c: Omit<Case, 'id' | 'created'>) => void; onCancel: () => void }> = ({ clients, onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [clientRecordId, setClientRecordId] = useState('');
  const [court, setCourt] = useState('');
  const [status, setStatus] = useState('open');
  const [notes, setNotes] = useState('');
  const clientName = clients.find(c => c.id === clientRecordId)?.name || '';
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 space-y-2">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Case title *" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <input value={caseNumber} onChange={e => setCaseNumber(e.target.value)} placeholder="Case number" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <select value={clientRecordId} onChange={e => setClientRecordId(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs">
        <option value="">— Select client —</option>
        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <input value={court} onChange={e => setCourt(e.target.value)} placeholder="Court" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs">
        <option value="open">Open</option>
        <option value="in-progress">In Progress</option>
        <option value="closed">Closed</option>
      </select>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes" rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
      <div className="flex gap-2">
        <button onClick={() => { if (title) onSave({ title, caseNumber, clientRecordId, clientName, court, status, notes }); }} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold">Save</button>
        <button onClick={onCancel} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600">Cancel</button>
      </div>
    </div>
  );
};

const CaseDetailPanel: React.FC<{
  caseData: Case;
  documents: CaseDocument[];
  sessions: ChatSession[];
  onUpload: (file: File) => void;
  onBack: () => void;
}> = ({ caseData, documents: initialDocs, sessions, onUpload, onBack }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [factsheet, setFactsheet] = useState<string | null>(null);
  const [factsheetLoading, setFactsheetLoading] = useState(false);
  const [factsheetMeta, setFactsheetMeta] = useState<{ generatedFrom: number; updatedAt: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [documents, setDocuments] = useState<CaseDocument[]>(initialDocs);

  // Load factsheet + refresh document statuses on mount
  useEffect(() => {
    fetch(`/api/cases/${caseData.id}/factsheet`).then(r => r.json()).then(d => {
      if (d.factsheet) { setFactsheet(d.factsheet); setFactsheetMeta({ generatedFrom: d.generatedFrom, updatedAt: d.updatedAt || d.createdAt }); }
    }).catch(() => {});
    // Refresh document statuses from server
    fetch(`/api/cases/${caseData.id}/documents`).then(r => r.json()).then(d => {
      if (d.documents) setDocuments(d.documents);
    }).catch(() => {});
  }, [caseData.id]);

  // Poll document statuses every 5s if any are pending/processing
  useEffect(() => {
    const hasPending = documents.some(d => d.ocrStatus === 'pending' || d.ocrStatus === 'processing');
    if (!hasPending) return;
    const interval = setInterval(() => {
      fetch(`/api/cases/${caseData.id}/documents`).then(r => r.json()).then(d => {
        if (d.documents) setDocuments(d.documents);
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [caseData.id, documents]);

  const generateFactsheet = async () => {
    let caseId = caseData.id;
    // If case has temp ID (never persisted), try to create it first
    if (caseId.startsWith('cs-')) {
      try {
        const createRes = await fetch('/api/cases', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: caseData.title || 'Untitled Case', caseNumber: caseData.caseNumber, court: caseData.court, status: caseData.status, notes: caseData.notes }) });
        const createData = await createRes.json();
        if (createData.success && createData.case) { caseId = createData.case.id; caseData.id = caseId; }
        else { setFactsheetLoading(false); return; }
      } catch { setFactsheetLoading(false); return; }
    }
    setFactsheetLoading(true);
    try {
      const res = await fetch(`/api/cases/${caseId}/factsheet/generate`, { method: 'POST' });
      const data = await res.json();
      if (data.success && data.factsheet) {
        setFactsheet(data.factsheet);
        setFactsheetMeta({ generatedFrom: data.generatedFrom, updatedAt: new Date().toISOString() });
      }
    } catch {}
    setFactsheetLoading(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const files = e.dataTransfer.files;
    for (let i = 0; i < files.length; i++) onUpload(files[i]);
  };

  const processedDocs = documents.filter(d => d.ocrStatus === 'success').length;
  const pendingDocs = documents.filter(d => d.ocrStatus === 'pending' || d.ocrStatus === 'processing').length;

  return (
    <div className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-gray-200/60 px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 -ml-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
            <ChevronLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">{caseData.title}</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                caseData.status === 'open' ? 'bg-emerald-100 text-emerald-700' :
                caseData.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                caseData.status === 'closed' ? 'bg-gray-200 text-gray-600' :
                'bg-orange-100 text-orange-700'
              }`}>{caseData.status}</span>
              {caseData.court && <span className="text-[10px] text-gray-500">🏛️ {caseData.court}</span>}
              {caseData.caseNumber && <span className="text-[10px] text-gray-500">#{caseData.caseNumber}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 max-w-4xl mx-auto">
        {/* Micro-Agent Status Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-4 sm:p-5 text-white shadow-lg shadow-purple-200">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Brain size={22} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold">Case Micro-Agent</h4>
              <p className="text-[11px] text-purple-100 mt-0.5">
                {processedDocs > 0
                  ? `Active — ${processedDocs} document${processedDocs > 1 ? 's' : ''} in knowledge base. Will assist AI with case-specific answers.`
                  : 'Spawned — upload documents to build case knowledge'}
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 text-[10px] bg-white/20 backdrop-blur px-2.5 py-1 rounded-full font-medium">
                <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse" /> Live
              </span>
            </div>
          </div>
        </div>

        {/* Case Info */}
        {(caseData.clientName || caseData.notes) && (
          <div className="bg-white rounded-2xl border border-gray-200/80 p-4 shadow-sm">
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
              {caseData.clientName && <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-lg">👤 {caseData.clientName}</span>}
              {caseData.court && <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-lg">🏛️ {caseData.court}</span>}
              {caseData.caseNumber && <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-lg">📋 #{caseData.caseNumber}</span>}
            </div>
            {caseData.notes && <p className="text-xs text-gray-500 mt-3 leading-relaxed">{caseData.notes}</p>}
          </div>
        )}

        {/* Documents Section */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <FileText size={16} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
                <span className="text-[10px] text-gray-500">{documents.length} file{documents.length !== 1 ? 's' : ''} • {processedDocs} processed</span>
              </div>
            </div>
            <button onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[11px] font-semibold flex items-center gap-1.5 hover:bg-blue-700 transition-colors shadow-sm">
              <Upload size={12} /> Upload
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden"
              onChange={e => { if (e.target.files) { for (let i = 0; i < e.target.files.length; i++) onUpload(e.target.files[i]); } e.target.value = ''; }} />
          </div>

          {/* Drag & Drop Zone */}
          <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
            className={`mx-4 sm:mx-5 mt-3 border-2 border-dashed rounded-xl p-5 text-center transition-all ${dragOver ? 'border-blue-400 bg-blue-50 scale-[1.01]' : 'border-gray-200 hover:border-gray-300'}`}>
            <Upload size={20} className={`mx-auto mb-1.5 ${dragOver ? 'text-blue-500' : 'text-gray-300'}`} />
            <p className="text-xs text-gray-500">Drag & drop FIRs, court orders, agreements here</p>
            <p className="text-[10px] text-gray-400 mt-0.5">JPEG, PNG, WebP, PDF • Max 20MB</p>
          </div>

          {/* Document List */}
          <div className="px-4 sm:px-5 py-3 space-y-2">
            {documents.length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-4">No documents yet — upload to build case knowledge</p>
            ) : documents.map(d => (
              <div key={d.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  d.ocrStatus === 'success' ? 'bg-emerald-100' : d.ocrStatus === 'processing' ? 'bg-blue-100' : d.ocrStatus === 'failed' ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  {d.ocrStatus === 'success' ? <CheckCircle size={14} className="text-emerald-600" /> :
                   d.ocrStatus === 'processing' ? <Loader2 size={14} className="text-blue-600 animate-spin" /> :
                   d.ocrStatus === 'failed' ? <AlertTriangle size={14} className="text-red-500" /> :
                   <FileText size={14} className="text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{d.originalName || d.fileName}</p>
                  <p className="text-[10px] text-gray-400">
                    {(d.fileSize / 1024).toFixed(0)} KB •{' '}
                    {d.ocrStatus === 'success' ? '✓ AI Processed' : d.ocrStatus === 'processing' ? 'Processing...' : d.ocrStatus === 'failed' ? 'Failed' : 'Queued'}
                  </p>
                </div>
              </div>
            ))}
            {pendingDocs > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-xl text-[11px] text-blue-700">
                <Loader2 size={12} className="animate-spin" /> {pendingDocs} document{pendingDocs > 1 ? 's' : ''} processing...
              </div>
            )}
          </div>
        </div>

        {/* AI Factsheet Section */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <Sparkles size={16} className="text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">AI Case Factsheet</h3>
                {factsheetMeta && <span className="text-[10px] text-gray-500">From {factsheetMeta.generatedFrom} docs • {new Date(factsheetMeta.updatedAt).toLocaleDateString()}</span>}
              </div>
            </div>
            <button onClick={generateFactsheet} disabled={factsheetLoading || processedDocs === 0}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-[11px] font-semibold flex items-center gap-1.5 hover:from-amber-600 hover:to-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm">
              {factsheetLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {factsheet ? 'Regenerate' : 'Generate'}
            </button>
          </div>

          <div className="px-4 sm:px-5 py-4">
            {processedDocs === 0 ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                  <Brain size={20} className="text-gray-300" />
                </div>
                <p className="text-xs text-gray-500 font-medium">Upload & process documents first</p>
                <p className="text-[10px] text-gray-400 mt-0.5">DocAI will analyze all documents to create a unified factsheet</p>
              </div>
            ) : !factsheet ? (
              <div className="text-center py-6 bg-amber-50/50 rounded-xl">
                <Sparkles size={24} className="mx-auto text-amber-400 mb-2" />
                <p className="text-xs text-gray-700 font-semibold">Ready to generate!</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{processedDocs} document{processedDocs > 1 ? 's' : ''} ready. Click "Generate" for a comprehensive case summary.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Action buttons */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-500">From {factsheetMeta?.generatedFrom} docs • {factsheetMeta?.updatedAt ? new Date(factsheetMeta.updatedAt).toLocaleDateString() : ''}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { navigator.clipboard.writeText(factsheet || ''); }}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-[11px] font-medium transition-colors">
                      📋 Copy
                    </button>
                    <button onClick={() => { const text = encodeURIComponent((factsheet || '').slice(0, 4000)); window.open(`https://wa.me/?text=${text}`, '_blank'); }}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg text-[11px] font-medium transition-colors">
                      💬 WhatsApp
                    </button>
                  </div>
                </div>
                {/* Factsheet content - MS Word style */}
                <div className="bg-white rounded-xl p-5 sm:p-6 border border-gray-200 shadow-sm max-h-[500px] overflow-y-auto" style={{ fontFamily: "'Times New Roman', Georgia, serif" }}>
                  <div className="text-[13px] leading-[1.8] text-gray-800" style={{ letterSpacing: '0.01em' }}>
                    {(factsheet || '').split('\n').map((line: string, i: number) => {
                      if (line.match(/^#{1,3}\s/)) {
                        const level = (line.match(/^#+/) || [''])[0].length;
                        const text = line.replace(/^#+\s*/, '');
                        if (level === 1) return <h2 key={i} style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '20px', marginBottom: '8px', borderBottom: '2px solid #1a1a1a', paddingBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{text}</h2>;
                        if (level === 2) return <h3 key={i} style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '16px', marginBottom: '6px', borderBottom: '1px solid #d1d5db', paddingBottom: '3px' }}>{text}</h3>;
                        return <h4 key={i} style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '12px', marginBottom: '4px', fontStyle: 'italic' }}>{text}</h4>;
                      }
                      if (line.match(/^\*\*.*\*\*$/)) return <p key={i} style={{ fontWeight: 'bold', marginTop: '12px', marginBottom: '4px', fontSize: '13px' }}>{line.replace(/\*\*/g, '')}</p>;
                      if (line.match(/^\d+\./)) return <p key={i} style={{ marginLeft: '20px', textIndent: '-20px', marginTop: '4px' }}>{line}</p>;
                      if (line.match(/^[-•]\s/)) return <p key={i} style={{ marginLeft: '20px', textIndent: '-12px', marginTop: '3px' }}>{line}</p>;
                      if (!line.trim()) return <br key={i} />;
                      return <p key={i} style={{ marginTop: '4px', textAlign: 'justify' }}>{line}</p>;
                    })}
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 flex items-center gap-1">
                  <Brain size={10} /> This factsheet powers the micro-agent. Upload new docs & regenerate to keep it current.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Linked Conversations */}
        {sessions.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <MessageSquare size={16} className="text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Conversations</h3>
                <span className="text-[10px] text-gray-500">{sessions.length} linked to this case</span>
              </div>
            </div>
            <div className="px-4 sm:px-5 py-3 space-y-2 max-h-60 overflow-y-auto">
              {sessions.map(s => (
                <div key={s.id} className="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare size={10} className="text-indigo-400" />
                    <span className="text-[9px] text-gray-400">{new Date(s.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-gray-800 font-medium truncate">{s.query}</div>
                  {s.response && <div className="text-[11px] text-gray-500 truncate mt-0.5">{s.response.slice(0, 120)}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Billing Panel — shows per-query billing with full detail table
const BillingPanel: React.FC<{ tokenBalance: { total: number; used: number; remaining: number; contract?: any } | null }> = ({ tokenBalance }) => {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await fetch('/api/tokens/history');
        const data = await res.json();
        if (data.success && data.history) setHistory(data.history);
      } catch {}
      setLoading(false);
    };
    loadHistory();
  }, []);

  const costPerToken = tokenBalance?.contract?.costPerToken || 50;
  const totalSpent = (tokenBalance?.used || 0) * costPerToken;
  const paged = history.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(history.length / PAGE_SIZE);

  const requestRecharge = async () => {
    try {
      await fetch('/api/tokens/recharge-request', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ amount: 100, notes: 'Requested from billing page' }) });
      toast.success('Recharge request submitted. Your administrator will process it shortly.');
    } catch { toast.error('Failed to submit recharge request'); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <h2 className="text-lg font-bold text-gray-900 mb-4">💰 Billing & Usage</h2>
      
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-gray-500 mb-1">Allocated</div>
          <div className="text-lg font-bold text-gray-600">{(tokenBalance?.total || 0).toFixed(1)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-gray-500 mb-1">Used</div>
          <div className="text-lg font-bold text-indigo-600">{(tokenBalance?.used || 0).toFixed(2)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-gray-500 mb-1">Remaining</div>
          <div className="text-lg font-bold text-green-600">{(tokenBalance?.remaining || 0).toFixed(2)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <div className="text-[10px] text-gray-500 mb-1">Total Spent</div>
          <div className="text-lg font-bold text-gray-900">₹{totalSpent.toFixed(0)}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-gray-100 rounded-full h-2 mb-4 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all" style={{ width: tokenBalance && tokenBalance.total > 0 ? `${Math.min((tokenBalance.used / tokenBalance.total) * 100, 100)}%` : '0%' }} />
      </div>

      {/* Rate + recharge */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-xs text-gray-500">Rate: ₹{costPerToken}/token (1 token ≈ 750 words)</div>
        <button onClick={requestRecharge} className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600">⚡ Request Recharge</button>
      </div>

      {/* Usage history table */}
      <h3 className="text-sm font-semibold text-gray-800 mb-2">📊 Query History</h3>
      {loading ? (
        <div className="text-center py-8 text-gray-400 text-xs">Loading...</div>
      ) : history.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-xs">No usage recorded yet.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-[10px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600">Time</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600">User</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600">Agent</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600">Endpoint</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600">Charged</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600">Remaining</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-600">Type</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((h: any, i: number) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{h.timestamp ? new Date(h.timestamp).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600 max-w-[80px] truncate">{h.userId || h.query?.slice(0, 15) || '—'}</td>
                    <td className="px-2 py-1.5 text-indigo-600 font-medium">{h.agentId || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-500">{h.endpoint || 'query/stream'}</td>
                    <td className="px-2 py-1.5 font-medium text-gray-900">{(h.tokensCharged || 0).toFixed(4)} <span className="text-gray-400">(₹{((h.tokensCharged || 0) * costPerToken).toFixed(1)})</span></td>
                    <td className="px-2 py-1.5 text-green-600">{h.remainingAfter !== null && h.remainingAfter !== undefined ? h.remainingAfter.toFixed(2) : '—'}</td>
                    <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[8px] font-semibold ${h.type === 'ocr' ? 'bg-blue-100 text-blue-700' : h.type === 'voice' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{h.type || 'query'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
              <span>Page {page + 1} of {totalPages} ({history.length} records)</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border rounded disabled:opacity-30">← Prev</button>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded disabled:opacity-30">Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Thread Detail Panel — loads full conversation from API
const ThreadDetailPanel: React.FC<{
  thread: ChatThread;
  clients: Client[];
  cases: Case[];
  isStandalone: boolean;
  onBack: () => void;
}> = ({ thread, clients, cases, isStandalone, onBack }) => {
  const [messages, setMessages] = useState(thread.messages);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load full messages from API if in standalone mode and messages are empty
    if (isStandalone && thread.messages.length === 0) {
      setLoading(true);
      fetch(`/api/conversations/session/${thread.id}`)
        .then(r => r.json())
        .then(data => {
          if (data.messages && data.messages.length > 0) {
            setMessages(data.messages.map((m: any) => ({
              role: m.role, content: m.content, timestamp: m.timestamp,
            })));
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setMessages(thread.messages);
    }
  }, [thread, isStandalone]);

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <button onClick={onBack} className="text-xs text-indigo-600 mb-3 flex items-center gap-1 hover:underline">← Back to Chat</button>
      <div className="flex items-center gap-3 mb-1">
        <h2 className="text-lg font-bold text-gray-900">{thread.title}</h2>
      </div>
      <div className="text-[10px] text-gray-400 mb-4 flex items-center gap-3">
        <span>📅 {new Date(thread.createdAt).toLocaleString()}</span>
        {thread.linkedClientId && (
          <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-[9px]">
            👤 {clients.find(c => c.id === thread.linkedClientId)?.name || 'Client'}
          </span>
        )}
        {thread.linkedCaseId && (
          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px]">
            📁 {cases.find(c => c.id === thread.linkedCaseId)?.title || 'Case'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400 text-xs">Loading conversation...</div>
      ) : messages.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-xs">No messages in this conversation</div>
      ) : (
        <div className="space-y-4 max-w-2xl">
          {messages.map((msg, i) => (
            <div key={i} className="flex gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${
                msg.role === 'user' ? 'bg-indigo-100 text-indigo-600' : 'bg-purple-100 text-purple-600'
              }`}>
                {msg.role === 'user' ? '👤' : '🤖'}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold text-gray-700">{msg.role === 'user' ? 'You' : 'AI Assistant'}</span>
                  <span className="text-[9px] text-gray-400">{msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}</span>
                </div>
                <div className={`text-xs text-gray-700 leading-relaxed whitespace-pre-wrap rounded-xl p-3 ${
                  msg.role === 'user' ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-200'
                }`}>
                  {msg.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══ Dashboard Panel ═══
const DashboardPanel: React.FC<{ appName: string; onNavigate: (p: Panel) => void }> = ({ appName, onNavigate }) => {
  const [briefing, setBriefing] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { fetch('/api/briefing').then(r => { if (!r.ok) throw new Error('not available'); return r.json(); }).then(d => setBriefing(d)).catch(() => setFailed(true)); }, []);
  
  if (!briefing && !failed) return <div className="flex-1 flex items-center justify-center text-gray-400">Loading...</div>;
  
  // Fallback for preview mode (no local server)
  if (failed) return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-1">Good morning, Advocate</h1>
        <p className="text-sm text-gray-500 mb-6">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center"><div className="text-2xl font-bold text-indigo-600">0</div><div className="text-[9px] text-indigo-500">Active Cases</div></div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center"><div className="text-2xl font-bold text-amber-600">0</div><div className="text-[9px] text-amber-500">Alerts</div></div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center"><div className="text-2xl font-bold text-green-600">—</div><div className="text-[9px] text-green-500">Tokens</div></div>
        </div>
        <p className="text-xs text-gray-400 text-center mb-6">Dashboard data will appear once the app is deployed and has cases/clients.</p>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => onNavigate('chat')} className="p-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all">💬 Chat with AI</button>
          <button onClick={() => onNavigate('cases')} className="p-4 bg-white border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:border-indigo-300 transition-all">📁 View Cases</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-1">{briefing.greeting}, Advocate</h1>
        <p className="text-sm text-gray-500 mb-6">{briefing.date}</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-indigo-600">{briefing.stats?.activeCases || 0}</div>
            <div className="text-[9px] text-indigo-500">Active Cases</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center cursor-pointer" onClick={() => onNavigate('alerts')}>
            <div className="text-2xl font-bold text-amber-600">{briefing.stats?.unreadAlerts || 0}</div>
            <div className="text-[9px] text-amber-500">Unread Alerts</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{briefing.stats?.tokensRemaining || 0}</div>
            <div className="text-[9px] text-green-500">Tokens</div>
          </div>
        </div>

        {/* Today's Hearings */}
        {briefing.hearingsToday?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <h3 className="text-xs font-bold text-red-800 mb-2 flex items-center gap-1"><Calendar size={12} /> Today's Hearings</h3>
            {briefing.hearingsToday.map((h: any, i: number) => (
              <div key={i} className="text-sm text-red-700">{h.title} — {h.court}</div>
            ))}
          </div>
        )}

        {/* Urgent Alerts */}
        {briefing.urgentAlerts?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <h3 className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> Urgent</h3>
            {briefing.urgentAlerts.map((a: any, i: number) => (
              <div key={i} className="text-sm text-amber-700 mb-1">{a.content}{a.caseName ? ` — ${a.caseName}` : ''}</div>
            ))}
          </div>
        )}

        {/* Upcoming Deadlines */}
        {briefing.upcomingDeadlines?.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <h3 className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1"><Clock size={12} /> Upcoming Deadlines</h3>
            {briefing.upcomingDeadlines.map((d: any, i: number) => (
              <div key={i} className="text-sm text-blue-700 mb-1">{d.content} — Due: {d.dueDate}{d.caseName ? ` (${d.caseName})` : ''}</div>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mt-6">
          <button onClick={() => onNavigate('chat')} className="p-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all">
            💬 Chat with AI
          </button>
          <button onClick={() => onNavigate('cases')} className="p-4 bg-white border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:border-indigo-300 transition-all">
            📁 View Cases
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══ Alerts Panel ═══
const AlertsPanel: React.FC = () => {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  useEffect(() => {
    fetch('/api/alerts').then(r => r.json()).then(d => setAlerts(d.alerts || [])).catch(() => {});
    fetch('/api/alerts/summary').then(r => r.json()).then(d => setSummary(d)).catch(() => {});
  }, []);
  const dismiss = async (id: string) => {
    await fetch(`/api/alerts/${id}/dismiss`, { method: 'PUT' });
    setAlerts(prev => prev.filter(a => a.id !== id));
  };
  return (
    <div className="flex-1 overflow-y-auto p-5">
      <h2 className="text-lg font-bold text-gray-900 mb-1">🔔 Alerts & Tasks</h2>
      {summary && (
        <div className="flex gap-3 mb-4 text-[10px]">
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-semibold">{summary.overdue || 0} Overdue</span>
          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-semibold">{summary.dueToday || 0} Today</span>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">{summary.upcoming7Days || 0} This Week</span>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-semibold">{summary.total || 0} Total</span>
        </div>
      )}
      {alerts.length === 0 ? (
        <div className="text-center text-gray-400 py-12">No pending alerts</div>
      ) : (
        <div className="space-y-2">
          {alerts.map(a => (
            <div key={a.id} className={`p-3 rounded-xl border flex items-start gap-3 ${a.priority === 'high' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-800">{a.content}</div>
                <div className="text-[10px] text-gray-500 mt-1">
                  {a.caseName && <span className="mr-2">📁 {a.caseName}</span>}
                  {a.alertDate && <span className="mr-2">📅 {a.alertDate}</span>}
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[8px]">{a.type}</span>
                </div>
              </div>
              <button onClick={() => dismiss(a.id)} className="text-[9px] px-2 py-1 bg-gray-100 text-gray-500 rounded hover:bg-gray-200">Dismiss</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeploymentPreviewPage;
